import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { ShieldStarIcon as ShieldStar, LockIcon as Lock, KeyIcon as Key, UserCheckIcon as UserCheck, RobotIcon as Robot, EyeIcon as Eye, DatabaseIcon as Database, FileLockIcon as FileLock, EnvelopeSimpleIcon as EnvelopeSimple, CheckCircleIcon as CheckCircle, WarningIcon as Warning, PrinterIcon as Printer, ArrowSquareOutIcon as ArrowSquareOut } from "@phosphor-icons/react";

/**
 * Admin → Security & Compliance Overview.
 *
 * A read-only inventory of every security control wired into the
 * platform. Designed so the operator can hand it (or print it) to an
 * insurance broker, lawyer, enterprise customer, or auditor without
 * having to dig through code. Each entry has:
 *   - Title (what the control is called)
 *   - Status pill (live / partial / planned)
 *   - One-sentence plain-English summary
 *   - File-of-truth in the repo so an auditor can verify the claim
 *
 * If a planned/partial item ships, flip its `status` here. The page
 * is intentionally hand-curated (not generated) so we don't accidentally
 * tell an auditor a control exists just because a file with a matching
 * name is in the repo.
 */

const CONTROLS = [
  {
    section: "Authentication",
    icon: Key,
    items: [
      {
        title: "JWT-based login with bcrypt password hashing",
        status: "live",
        summary:
          "Passwords are never stored in plaintext. Hashed with bcrypt; JWTs signed with a server-side secret rotated separately from app deploys.",
        evidence: "backend/routers/auth.py · backend/services/auth.py",
      },
      {
        title: "Google OAuth (Emergent-managed)",
        status: "live",
        summary:
          "Users can sign in with Google. We never receive Google passwords; only an identity assertion from Google's token endpoint.",
        evidence: "backend/routers/auth.py (oauth callback)",
      },
      {
        title: "Forgot-password & email-verified reset",
        status: "live",
        summary:
          "Reset tokens are single-use, expire in 60 minutes, delivered via Resend. Original password cannot be recovered — only a new one set.",
        evidence: "backend/routers/auth.py (password_reset_*)",
      },
      {
        title: "Two-factor authentication (TOTP)",
        status: "live",
        summary:
          "Users can enable Google Authenticator / Authy / 1Password via PyOTP, with one-time recovery codes for lost-phone scenarios.",
        evidence: "backend/routers/auth_2fa.py",
      },
    ],
  },
  {
    section: "Anti-bot & abuse prevention",
    icon: Robot,
    items: [
      {
        title: "Cloudflare Turnstile CAPTCHA",
        status: "live",
        summary:
          "Login, signup, password reset, and contact forms are CAPTCHA-gated. Server validates the Turnstile token against Cloudflare before issuing tokens.",
        evidence: "backend/services/captcha.py",
      },
      {
        title: "Brute-force protection (per IP + per account)",
        status: "live",
        summary:
          "Repeated failed logins trigger progressive backoff at the account and IP level. Counters auto-expire so legitimate users aren't permanently locked.",
        evidence: "backend/routers/auth.py · login_brute_force_counters",
      },
      {
        title: "HMAC-signed back-channel admin operations",
        status: "live",
        summary:
          "Privileged jobs (marketing recordings, showcase mode, internal automation) use HMAC-SHA256 signatures with timestamped replay protection.",
        evidence: "backend/services/captcha.py (_verify_recorder_hmac) · backend/services/showcase.py",
      },
      {
        title: "Cloudflare WAF (default ruleset)",
        status: "live",
        summary:
          "Production sits behind Cloudflare with default OWASP-aligned rules. Upgrade to Cloudflare Pro/Business adds OWASP Top 10 + custom rules.",
        evidence: "Cloudflare dashboard (external)",
      },
    ],
  },
  {
    section: "Authorization & access control",
    icon: UserCheck,
    items: [
      {
        title: "Role-based admin gating",
        status: "live",
        summary:
          "Every `/admin/*` route requires `role:'admin'`. Frontend hides it; backend `_admin_only` re-checks on every request so client-side tampering can't escalate.",
        evidence: "backend/deps.py · _admin_only",
      },
      {
        title: "Owner-scoped CRUD",
        status: "live",
        summary:
          "Users can only edit dogs, litters, listings, and storefronts they own. Backend enforces ownership on every PATCH/DELETE — IDs alone can't grant access.",
        evidence: "backend/routers/dogs.py · backend/routers/marketplace/listings.py",
      },
      {
        title: "Auth-gated marketplace contact",
        status: "live",
        summary:
          "Anonymous visitors can browse listings, but contacting a seller requires sign-in — prevents drive-by spam to breeders' inboxes.",
        evidence: "backend/routers/marketplace/inquiries.py",
      },
      {
        title: "Stripe Connect destination charges",
        status: "live",
        summary:
          "Buyer funds flow to the seller's Stripe account via Connect, not into your platform account. Your blast radius if compromised is dramatically smaller.",
        evidence: "backend/routers/stripe_connect.py",
      },
    ],
  },
  {
    section: "Data security",
    icon: Database,
    items: [
      {
        title: "Secrets in environment variables, not code",
        status: "live",
        summary:
          "API keys (Stripe, Resend, Printify, Daily, Cloudflare, etc.) live in `backend/.env` and the Emergent secrets panel — never committed to GitHub.",
        evidence: "backend/.env (gitignored)",
      },
      {
        title: "MongoDB `_id` always excluded",
        status: "live",
        summary:
          "Every API response projects out the BSON `_id`. Prevents accidental disclosure of MongoDB internals and lets us regenerate IDs without breaking clients.",
        evidence: "Convention enforced across all routers",
      },
      {
        title: "TLS / HTTPS everywhere",
        status: "live",
        summary:
          "Production is HTTPS-only, terminated at Cloudflare with auto-renewed certificates. HTTP is auto-redirected.",
        evidence: "Cloudflare edge (external)",
      },
      {
        title: "Object storage for user uploads",
        status: "live",
        summary:
          "Dog photos, kennel photos, legal PDFs, and marketing videos live in persistent object storage with signed URLs, not in the database or local disk.",
        evidence: "backend/routers/storage.py",
      },
    ],
  },
  {
    section: "Privacy & compliance",
    icon: FileLock,
    items: [
      {
        title: "Cookie consent banner",
        status: "live",
        summary:
          "Visitors choose essential vs. all cookies on first visit. Choice is honored across the session and recorded in their account if signed in.",
        evidence: "frontend/src/components/CookieBanner.jsx",
      },
      {
        title: "Right-to-delete & data export",
        status: "live",
        summary:
          "Users can request a full data export (JSON of everything we hold) or full deletion under Settings → Privacy. Honored within 30 days per GDPR/CCPA.",
        evidence: "backend/routers/privacy.py",
      },
      {
        title: "Comprehensive Legal Bundle",
        status: "live",
        summary:
          "Terms, Privacy, Cookie Notice, AI Use Disclosure, Auto-Renew Disclosure, Photo Content License — all live and downloadable as a single ZIP.",
        evidence: "backend/routers/legal_bundle.py · /admin/legal-bundle",
        link: "/admin/legal-bundle",
      },
      {
        title: "Audit logs on sensitive admin actions",
        status: "live",
        summary:
          "Transfers, verifications, payment overrides, role changes, and refunds are logged with actor + timestamp + before/after diff for review.",
        evidence: "backend/services/audit_log.py",
      },
    ],
  },
  {
    section: "Payment & financial security",
    icon: Lock,
    items: [
      {
        title: "PCI-DSS-conformant Stripe Elements",
        status: "live",
        summary:
          "Card numbers never touch our servers — they're tokenized client-side by Stripe Elements. We store only the Stripe `payment_intent` ID.",
        evidence: "frontend/src/components/StripeCheckout.jsx",
      },
      {
        title: "Stripe webhook signature verification",
        status: "live",
        summary:
          "Every Stripe webhook (`payment_intent.succeeded`, `account.updated`, etc.) is verified against Stripe's signature header before any state change.",
        evidence: "backend/routers/stripe_webhook.py",
      },
      {
        title: "Stripe Radar fraud rules",
        status: "live",
        summary:
          "Card-testing protection, velocity rules, and elevated-risk holds are configured in the Stripe dashboard with our tuning.",
        evidence: "/admin/stripe-radar",
        link: "/admin/stripe-radar",
      },
      {
        title: "Refund escrow & dispute workflow",
        status: "live",
        summary:
          "Buyer funds are held in escrow until ownership transfer confirmation. Disputes route to admin review before any release.",
        evidence: "backend/routers/escrow.py · /admin/escrow",
        link: "/admin/escrow",
      },
    ],
  },
  {
    section: "Planned / partial",
    icon: Warning,
    items: [
      {
        title: "Formal penetration test",
        status: "planned",
        summary:
          "Engage a third-party security firm for an external pen-test once user volume justifies the spend (typically $5–15k engagement).",
        evidence: "Backlog item",
      },
      {
        title: "SOC 2 Type II",
        status: "planned",
        summary:
          "Only if/when selling to enterprise/B2B customers. Major investment ($20–40k initial + ongoing audit fees). Not required for D2C.",
        evidence: "Backlog item",
      },
      {
        title: "Session-fingerprint device binding",
        status: "planned",
        summary:
          "P2 backlog. Tracks a hashed device fingerprint with each session to detect Buyer Pass account-sharing without storing PII.",
        evidence: "Backlog item",
      },
      {
        title: "Kennel-name impersonation check",
        status: "planned",
        summary:
          "P2 backlog. Levenshtein-distance comparison at new-kennel signup to flag near-clones of existing verified kennels.",
        evidence: "Backlog item",
      },
    ],
  },
];

const STATUS_STYLE = {
  live: {
    label: "Live",
    cls: "bg-emerald-50 text-emerald-800 border-emerald-200",
    Icon: CheckCircle,
  },
  partial: {
    label: "Partial",
    cls: "bg-amber-50 text-amber-800 border-amber-200",
    Icon: Warning,
  },
  planned: {
    label: "Planned",
    cls: "bg-stone-100 text-stone-700 border-stone-300",
    Icon: Warning,
  },
};

export default function AdminSecurity() {
  // Quick filter so the operator can hand the page to an auditor and
  // toggle "show only live controls" / "show only what's outstanding"
  // without scrolling.
  const [filter, setFilter] = useState("all");

  const sections = useMemo(() => {
    return CONTROLS.map((s) => ({
      ...s,
      items: s.items.filter((it) => filter === "all" || it.status === filter),
    })).filter((s) => s.items.length > 0);
  }, [filter]);

  const counts = useMemo(() => {
    const c = { live: 0, partial: 0, planned: 0 };
    CONTROLS.forEach((s) => s.items.forEach((it) => { c[it.status] = (c[it.status] || 0) + 1; }));
    return c;
  }, []);

  return (
    <AppShell title="Security & Compliance Overview">
      <div className="max-w-4xl mx-auto px-4 pb-24" data-testid="admin-security-page">
        {/* Header */}
        <div className="mb-8 pt-6 print:pt-2">
          <Link
            to="/admin"
            className="text-[12px] text-ink/60 hover:text-ink mb-3 inline-flex items-center gap-1 print:hidden"
          >
            ← Admin console
          </Link>
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-[#FBF7EC] border border-[#C9A227]/30 p-3 mt-1 print:hidden">
              <ShieldStar size={26} weight="duotone" className="text-[#8A6A00]" />
            </div>
            <div>
              <h1 className="font-spectral font-light text-3xl md:text-4xl tracking-tight leading-tight text-ink">
                Security &amp; Compliance Overview
              </h1>
              <p className="mt-3 text-ink/70 text-[15px] leading-relaxed max-w-2xl">
                Every security and compliance control wired into the platform,
                with the file-of-truth so an insurance broker, lawyer, or auditor
                can verify each claim. Print this page (Cmd/Ctrl+P) to hand off
                a paper copy.
              </p>
            </div>
          </div>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="security-counts">
          <StatPill label="Live controls" value={counts.live} tone="live" />
          <StatPill label="Partial" value={counts.partial || 0} tone="partial" />
          <StatPill label="Planned" value={counts.planned || 0} tone="planned" />
        </div>

        {/* Filter chips + print */}
        <div className="flex flex-wrap items-center gap-2 mb-8 print:hidden">
          {["all", "live", "partial", "planned"].map((k) => (
            <Button
              key={k}
              size="sm"
              variant={filter === k ? "default" : "outline"}
              onClick={() => setFilter(k)}
              data-testid={`security-filter-${k}`}
              className={`rounded-full ${filter === k ? "bg-ink text-white" : ""}`}
            >
              {k === "all" ? "All controls" : STATUS_STYLE[k].label}
            </Button>
          ))}
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.print()}
            data-testid="security-print-button"
            className="rounded-full"
          >
            <Printer size={14} weight="bold" className="mr-1.5" />
            Print
          </Button>
        </div>

        {/* Sections */}
        <div className="space-y-10">
          {sections.map((s) => (
            <Section key={s.section} icon={s.icon} title={s.section} items={s.items} />
          ))}
        </div>

        {/* Help-the-broker footer */}
        <div className="mt-12 rounded-2xl border border-[#C9A227]/30 bg-[#FBF7EC] p-5 print:hidden">
          <div className="flex items-start gap-3">
            <EnvelopeSimple size={20} weight="duotone" className="text-[#8A6A00] mt-0.5 shrink-0" />
            <div>
              <p className="text-[14px] font-semibold text-ink">
                Need a written statement for your insurance broker or counsel?
              </p>
              <p className="text-[12.5px] text-ink/70 mt-1.5 leading-relaxed">
                Print this page or screenshot specific sections. If a broker
                asks something this page doesn't answer (e.g. "do you encrypt
                PII at rest?"), reach out via your standard support channel and
                we'll pull the exact answer from the codebase.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function StatPill({ label, value, tone }) {
  const cls = STATUS_STYLE[tone]?.cls || "bg-stone-100 text-stone-700 border-stone-300";
  return (
    <div className={`rounded-xl border p-4 ${cls}`}>
      <p className="text-[11px] uppercase font-bold tracking-wider opacity-80">{label}</p>
      <p className="text-3xl font-spectral font-light tracking-tight mt-1">{value}</p>
    </div>
  );
}

function Section({ icon: Icon, title, items }) {
  return (
    <section>
      <div className="flex items-center gap-2.5 mb-3">
        <Icon size={20} weight="duotone" className="text-[#8A6A00]" />
        <h2 className="font-spectral font-light text-xl md:text-2xl tracking-tight text-ink">
          {title}
        </h2>
      </div>
      <div className="rounded-xl border border-stone-200 divide-y divide-stone-100 bg-white">
        {items.map((it) => (
          <ControlRow key={it.title} item={it} />
        ))}
      </div>
    </section>
  );
}

function ControlRow({ item }) {
  const s = STATUS_STYLE[item.status] || STATUS_STYLE.planned;
  const StatusIcon = s.Icon;
  return (
    <div
      className="p-4 flex items-start gap-4"
      data-testid={`security-control-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
    >
      <div className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wider ${s.cls}`}>
        <StatusIcon size={11} weight="bold" />
        {s.label}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-ink text-[14px] leading-snug">
          {item.title}
        </p>
        <p className="text-[12.5px] text-ink/70 leading-relaxed mt-1">
          {item.summary}
        </p>
        <p className="text-[11px] text-ink/50 mt-1.5 font-mono break-all">
          <Eye size={10} weight="bold" className="inline mr-1 -mt-0.5" />
          {item.evidence}
          {item.link && (
            <Link
              to={item.link}
              className="ml-2 inline-flex items-center gap-0.5 text-[#8A6A00] hover:underline font-sans not-italic"
            >
              Open <ArrowSquareOut size={10} weight="bold" />
            </Link>
          )}
        </p>
      </div>
    </div>
  );
}
